import { useState, useEffect, useCallback } from 'react';
import {
  isCapacitorNative,
  checkShareIntent,
  contentUriToFile,
  getContentTypeFromMime,
  type SendIntentResult,
} from '@/lib/capacitor';

export interface SharedMedia {
  file: File;
  type: 'photo' | 'video';
}

interface UseShareIntentResult {
  sharedMedia: SharedMedia | null;
  isProcessing: boolean;
  error: string | null;
  clearSharedMedia: () => void;
}

/**
 * Hook to handle incoming share intents on Android.
 * When a user shares a photo or video to the app, this hook
 * captures it and converts it to a File object.
 */
export function useShareIntent(): UseShareIntentResult {
  const [sharedMedia, setSharedMedia] = useState<SharedMedia | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const processIntent = useCallback(async (intent: SendIntentResult) => {
    console.log('[useShareIntent] Processing intent:', JSON.stringify(intent, null, 2));
    
    // Get the URL (content URI) and type from the intent
    const url = intent.url;
    const mimeType = intent.type;

    if (!url || !mimeType) {
      console.log('[useShareIntent] Share intent has no URL or type:', intent);
      return;
    }

    // Determine if it's a photo or video
    const contentType = getContentTypeFromMime(mimeType);
    console.log('[useShareIntent] Detected content type:', contentType, 'from mimeType:', mimeType);
    
    if (!contentType) {
      console.log('[useShareIntent] Unsupported MIME type:', mimeType);
      setError(`Unsupported file type: ${mimeType}`);
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Convert the content URI to a File object
      console.log('[useShareIntent] Converting URI to file...');
      const file = await contentUriToFile(url, mimeType);
      if (!file) {
        throw new Error('Failed to load shared file');
      }

      console.log('[useShareIntent] Setting shared media, file size:', file.size);
      setSharedMedia({ file, type: contentType });
    } catch (err) {
      console.error('[useShareIntent] Failed to process shared file:', err);
      setError(err instanceof Error ? err.message : 'Failed to process shared file');
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const checkForIntent = useCallback(async () => {
    console.log('[useShareIntent] Checking for share intent...');
    
    if (!isCapacitorNative()) {
      console.log('[useShareIntent] Not native platform, skipping');
      return;
    }

    try {
      const intent = await checkShareIntent();
      console.log('[useShareIntent] Got intent:', intent ? 'yes' : 'no');
      if (intent) {
        await processIntent(intent);
      }
    } catch (err) {
      console.error('[useShareIntent] Error checking share intent:', err);
    }
  }, [processIntent]);

  // Check for share intent on mount
  useEffect(() => {
    checkForIntent();
  }, [checkForIntent]);

  // Listen for app resume events and sendIntentReceived to check for new intents
  useEffect(() => {
    if (!isCapacitorNative()) {
      return;
    }

    // Handler for checking intents
    const handleCheckIntent = async () => {
      await checkForIntent();
    };

    // Handler for visibility change (works in Capacitor WebView)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleCheckIntent();
      }
    };

    // Add listener for visibility change
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Add listener for sendIntentReceived event (fired by send-intent plugin
    // when the app receives a share intent while already running)
    window.addEventListener('sendIntentReceived', handleCheckIntent);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('sendIntentReceived', handleCheckIntent);
    };
  }, [checkForIntent]);

  const clearSharedMedia = useCallback(() => {
    setSharedMedia(null);
    setError(null);
    // Note: We no longer call finishShareIntent() here because that closes the app.
    // The app should only close after a successful upload when triggered by share intent.
    // finishShareIntent() is now called explicitly in SharePage after upload success.
  }, []);

  return {
    sharedMedia,
    isProcessing,
    error,
    clearSharedMedia,
  };
}

